import { useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import { env } from '@/api/config/env';
import { bootstrapSession } from '@/app/session';
import { authApi, refusalFor, type SignInRefusal } from '@/modules/auth/services/api';
import { LoginForm } from './login-form';

type FormData = {
  email: string;
  password: string;
};

/**
 * The message shown for each refusal.
 *
 * Deliberately not the provider's raw code: a refusal the user cannot act on is
 * noise. The distinctions that ARE preserved are the ones with a different NEXT
 * STEP — "your identity is not verified yet" is a different problem from a wrong
 * password, and "this address is not trusted by the server" is a different problem
 * from both: the credential was never even looked at, and no amount of retyping it
 * will help.
 */
const REFUSAL_MESSAGES: Readonly<Record<SignInRefusal, string>> = {
  invalid_credentials: 'That email and password do not match an account.',
  email_not_verified: 'This identity has not been verified yet.',
  untrusted_origin: 'The server does not trust this address. Ask an administrator to add this console to the trusted origins.',
  rate_limited: 'Too many sign-in attempts. Wait a moment and try again.',
  unavailable: 'Sign-in is unavailable right now. Try again shortly.',
};

/**
 * Public sign-in page.
 *
 * Not inside `shellRoute`, so it is reachable without a session.
 *
 * The submit path is the REAL one: the provider is asked to verify the credential,
 * and the session is then read back from the server — the same read the app makes
 * on every cold start. The session is NEVER seeded from what the user typed: a
 * form that decides who you are is not a sign-in.
 *
 * The redirect target comes from `?redirect=`, which the guard validates on the way
 * back. It is not trusted here either: the server decides where the session goes
 * by refusing it, not by being told.
 */
export function LoginPage() {
  const search = useSearch({ from: '/login' });
  const redirect = (search.redirect as string | undefined) ?? '/overview';
  const [form, setForm] = useState<FormData>({ email: '', password: '' });
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // A console is bound to ONE tenant, and that binding is what the `X-Tenant-Slug`
  // header carries. The platform resolves a session AGAINST a tenant, so a console
  // with no binding cannot obtain one at all — the credential is never the problem.
  //
  // The guard is AFTER the hooks on purpose: the hook order must not depend on a
  // build-time variable.
  if (env.tenantSlug === undefined) {
    return <UnboundConsole />;
  }

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setPending(true);

    try {
      const outcome = await authApi.signIn({ email: form.email, password: form.password });
      if (!outcome.ok) {
        setError(REFUSAL_MESSAGES[outcome.refusal]);
        return;
      }

      // The session is READ BACK from the server, not assumed. If the provider
      // accepted the credential but the platform refuses the session, the user
      // must not be walked into an authenticated shell.
      const snapshot = await bootstrapSession('full');
      if (snapshot.status !== 'authenticated') {
        setError('Signed in, but the platform did not return a session. Try again.');
        return;
      }

      globalThis.location?.assign(redirect);
    } catch (error) {
      // The sign-in step SUCCEEDED and something after it failed — the session read-back
      // is the usual one. The failure is reported in the SAME vocabulary as the sign-in
      // itself, because a throttle and an outage are different problems with different
      // answers: one waits, the other does not. Reporting every failure here as "the
      // service is unavailable" sends an operator to the wrong desk.
      setError(REFUSAL_MESSAGES[refusalFor(error)]);
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-surface px-4">
      <LoginForm
        form={form}
        setForm={setForm}
        error={error}
        pending={pending}
        onSubmit={handleSubmit}
      />
    </main>
  );
}

/**
 * Shown instead of the sign-in form when the console names no tenant.
 *
 * WHY NOT SHOW THE FORM ANYWAY
 * ----------------------------
 * The platform resolves a session against a tenant, and the tenant reaches it in
 * the `X-Tenant-Slug` header — which this build cannot fill. So the session read
 * after a successful sign-in comes back 401 and the operator is told the platform
 * "did not return a session", which points at the server when the fault is the
 * console's own configuration. And a form that cannot succeed asks for a
 * credential it is going to throw away: the same lie as a form that accepts
 * anything, only slower.
 */
function UnboundConsole() {
  return (
    <main className="min-h-screen flex items-center justify-center bg-surface px-4">
      <div className="w-full max-w-md space-y-4">
        <h1 className="text-2xl font-bold text-content">This console has no tenant</h1>
        <p className="text-content-muted">
          The Mana Locals console is bound to one tenant, and this build names none.
          The platform resolves a session against a tenant, so there is no sign-in to
          make here.
        </p>
        <p className="text-sm text-content-muted/60">
          Start it naming one —{' '}
          <code className="font-mono">VITE_TENANT_SLUG=demo-products pnpm dev</code> — and
          reload this page.
        </p>
      </div>
    </main>
  );
}
